import { getCategoryHierarchyApi, updateCategoryOptionsApi, updateCategoryLabelApi, createCategoryItemApi } from '../../util/api';

// ================ Action types ================ //

export const FETCH_CATEGORY_HIERARCHY_REQUEST = 'app/AdminPanelCategoryPage/FETCH_CATEGORY_HIERARCHY_REQUEST';
export const FETCH_CATEGORY_HIERARCHY_SUCCESS = 'app/AdminPanelCategoryPage/FETCH_CATEGORY_HIERARCHY_SUCCESS';
export const FETCH_CATEGORY_HIERARCHY_ERROR = 'app/AdminPanelCategoryPage/FETCH_CATEGORY_HIERARCHY_ERROR';

export const UPDATE_CATEGORY_OPTIONS_REQUEST = 'app/AdminPanelCategoryPage/UPDATE_CATEGORY_OPTIONS_REQUEST';
export const UPDATE_CATEGORY_OPTIONS_SUCCESS = 'app/AdminPanelCategoryPage/UPDATE_CATEGORY_OPTIONS_SUCCESS';
export const UPDATE_CATEGORY_OPTIONS_ERROR = 'app/AdminPanelCategoryPage/UPDATE_CATEGORY_OPTIONS_ERROR';

export const UPDATE_CATEGORY_LABEL_REQUEST = 'app/AdminPanelCategoryPage/UPDATE_CATEGORY_LABEL_REQUEST';
export const UPDATE_CATEGORY_LABEL_SUCCESS = 'app/AdminPanelCategoryPage/UPDATE_CATEGORY_LABEL_SUCCESS';
export const UPDATE_CATEGORY_LABEL_ERROR = 'app/AdminPanelCategoryPage/UPDATE_CATEGORY_LABEL_ERROR';

export const CREATE_CATEGORY_ITEM_REQUEST = 'app/AdminPanelCategoryPage/CREATE_CATEGORY_ITEM_REQUEST';
export const CREATE_CATEGORY_ITEM_SUCCESS = 'app/AdminPanelCategoryPage/CREATE_CATEGORY_ITEM_SUCCESS';
export const CREATE_CATEGORY_ITEM_ERROR = 'app/AdminPanelCategoryPage/CREATE_CATEGORY_ITEM_ERROR';

// ================ Reducer ================ //

const initialState = {
  categoryData: [],
  categoryDataInProgress: false,
  categoryDataError: null,
  updateInProgress: false,
  updateError: null,
  updateLabelInProgress: false,
  updateLabelError: null,
  createInProgress: false,
  createError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_CATEGORY_HIERARCHY_REQUEST:
      return {
        ...state,
        categoryDataInProgress: true,
        categoryDataError: null,
      };
    case FETCH_CATEGORY_HIERARCHY_SUCCESS:
      return {
        ...state,
        categoryData: payload,
        categoryDataInProgress: false,
      };
    case FETCH_CATEGORY_HIERARCHY_ERROR:
      return {
        ...state,
        categoryDataInProgress: false,
        categoryDataError: payload,
      };
    case UPDATE_CATEGORY_OPTIONS_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateError: null,
      };
    case UPDATE_CATEGORY_OPTIONS_SUCCESS:
      return {
        ...state,
        updateInProgress: false,
      };
    case UPDATE_CATEGORY_OPTIONS_ERROR:
      return {
        ...state,
        updateInProgress: false,
        updateError: payload,
      };
    case UPDATE_CATEGORY_LABEL_REQUEST:
      return {
        ...state,
        updateLabelInProgress: true,
        updateLabelError: null,
      };
    case UPDATE_CATEGORY_LABEL_SUCCESS:
      return {
        ...state,
        updateLabelInProgress: false,
      };
    case UPDATE_CATEGORY_LABEL_ERROR:
      return {
        ...state,
        updateLabelInProgress: false,
        updateLabelError: payload,
      };
    case CREATE_CATEGORY_ITEM_REQUEST:
      return {
        ...state,
        createInProgress: true,
        createError: null,
      };
    case CREATE_CATEGORY_ITEM_SUCCESS:
      return {
        ...state,
        createInProgress: false,
      };
    case CREATE_CATEGORY_ITEM_ERROR:
      return {
        ...state,
        createInProgress: false,
        createError: payload,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchCategoryHierarchyRequest = () => ({
  type: FETCH_CATEGORY_HIERARCHY_REQUEST,
});

export const fetchCategoryHierarchySuccess = data => ({
  type: FETCH_CATEGORY_HIERARCHY_SUCCESS,
  payload: data,
});

export const fetchCategoryHierarchyError = error => ({
  type: FETCH_CATEGORY_HIERARCHY_ERROR,
  payload: error,
});

export const updateCategoryOptionsRequest = () => ({
  type: UPDATE_CATEGORY_OPTIONS_REQUEST,
});

export const updateCategoryOptionsSuccess = () => ({
  type: UPDATE_CATEGORY_OPTIONS_SUCCESS,
});

export const updateCategoryOptionsError = error => ({
  type: UPDATE_CATEGORY_OPTIONS_ERROR,
  payload: error,
});

export const updateCategoryLabelRequest = () => ({
  type: UPDATE_CATEGORY_LABEL_REQUEST,
});

export const updateCategoryLabelSuccess = () => ({
  type: UPDATE_CATEGORY_LABEL_SUCCESS,
});

export const updateCategoryLabelError = error => ({
  type: UPDATE_CATEGORY_LABEL_ERROR,
  payload: error,
});

export const createCategoryItemRequest = () => ({
  type: CREATE_CATEGORY_ITEM_REQUEST,
});

export const createCategoryItemSuccess = () => ({
  type: CREATE_CATEGORY_ITEM_SUCCESS,
});

export const createCategoryItemError = error => ({
  type: CREATE_CATEGORY_ITEM_ERROR,
  payload: error,
});

// ================ Thunks ================ //

export const fetchCategoryHierarchy = () => async dispatch => {
  dispatch(fetchCategoryHierarchyRequest());

  try {
    const result = await getCategoryHierarchyApi();

    if (result.success) {
      dispatch(fetchCategoryHierarchySuccess(result.data));
    } else {
      dispatch(fetchCategoryHierarchyError(result.message || 'Failed to fetch data'));
    }
  } catch (error) {
    dispatch(fetchCategoryHierarchyError(error.message || 'Failed to fetch data'));
  }
};

export const loadData = () => {
  return fetchCategoryHierarchy();
};

export const updateCategoryOptions = (dynamicFieldId, options) => async dispatch => {
  dispatch(updateCategoryOptionsRequest());

  try {
    const result = await updateCategoryOptionsApi({ dynamicFieldId, options });

    if (result.success) {
      dispatch(updateCategoryOptionsSuccess());
      return result;
    } else {
      dispatch(updateCategoryOptionsError(result.message || 'Failed to update options'));
      throw new Error(result.message);
    }
  } catch (error) {
    dispatch(updateCategoryOptionsError(error.message || 'Failed to update options'));
    throw error;
  }
};

export const updateCategoryLabel = (type, id, label, isDelete, isGroupBy) => async dispatch => {
  dispatch(updateCategoryLabelRequest());

  try {
    const result = await updateCategoryLabelApi({ type, id, label, isDelete, isGroupBy });

    if (result.success) {
      dispatch(updateCategoryLabelSuccess());
      return result;
    } else {
      dispatch(updateCategoryLabelError(result.message || 'Failed to update label'));
      throw new Error(result.message);
    }
  } catch (error) {
    dispatch(updateCategoryLabelError(error.message || 'Failed to update label'));
    throw error;
  }
};

export const createCategoryItem = (type, label, parentId, isGroupBy) => async dispatch => {
  dispatch(createCategoryItemRequest());

  try {
    const result = await createCategoryItemApi({ type, label, parentId, isGroupBy });

    if (result.success) {
      dispatch(createCategoryItemSuccess());
      return result;
    } else {
      dispatch(createCategoryItemError(result.message || 'Failed to create item'));
      throw new Error(result.message);
    }
  } catch (error) {
    dispatch(createCategoryItemError(error.message || 'Failed to create item'));
    throw error;
  }
};
